import { cn } from '@foundation/utils/cn';

import {
  HeadlessSelect,
  HeadlessSelectOption,
  HeadlessSelectProps,
} from '@design-system/headless-ui';
import { ChevronLeft } from '@design-system/icons';

export type SelectOption<Value> = HeadlessSelectOption<Value>;

export type SelectProps<Value> = Omit<
  HeadlessSelectProps<Value>,
  'styles' | 'renderIcon' | 'renderItemIndicator'
>;

function SelectComponent<Value>(props: SelectProps<Value>) {
  return (
    <HeadlessSelect
      {...props}
      renderIcon={() => (
        <ChevronLeft
          size={20}
          className="-rotate-90"
        />
      )}
      renderItemIndicator={() => (
        <svg
          viewBox="0 0 16 16"
          fill="none"
          className="size-4"
          aria-hidden="true"
        >
          <path
            d="M3.5 8.5 6.5 11.5 12.5 4.5"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      styles={{
        label: cn(
          'pointer-events-none absolute left-4 top-2 hidden text-xs leading-normal tracking-tightest text-pmui-fg-secondary',
          'data-filled:block',
        ),
        requiredIndicator: 'text-pmui-fg-status-negative-default',
        trigger: cn(
          'h-14 cursor-pointer rounded-md border border-pmui-border-primary bg-pmui-bg-primary pl-4 pr-10 text-md leading-6 tracking-tightest text-pmui-fg-placeholder shadow-sm',
          'data-filled:text-pmui-fg-primary',
          'focus-visible:outline-hidden focus-visible:border-pmui-border-focus-ring-focus',
          'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-pmui-border-focus-ring-focus',
          'disabled:cursor-not-allowed disabled:bg-pmui-interactive-bg-disabled disabled:text-pmui-interactive-fg-disabled disabled:shadow-none',
          'data-invalid:border-pmui-border-status-negative data-invalid:shadow-none',
          'data-invalid:ring-1 data-invalid:ring-inset data-invalid:ring-pmui-border-status-negative',
          'data-filled:pt-4',
        ),
        value: 'data-[placeholder]:text-pmui-fg-placeholder',
        icon: 'absolute right-4 top-1/2 -translate-y-1/2 shrink-0 text-pmui-fg-secondary',
        popup: cn(
          'w-(--anchor-width) max-h-[min(18rem,var(--available-height))] overflow-y-auto rounded-md border border-pmui-border-primary',
          'bg-pmui-bg-primary p-1 shadow-md',
        ),
        item: cn(
          'flex cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 text-sm leading-5 tracking-tightest text-pmui-fg-primary',
          'data-highlighted:bg-pmui-bg-secondary',
          'data-disabled:cursor-not-allowed data-disabled:opacity-40',
        ),
        itemIndicator: 'shrink-0 text-pmui-fg-brand-root',
        helperText:
          'text-sm leading-5 tracking-tightest text-pmui-fg-secondary',
        error:
          'text-sm leading-5 tracking-tightest text-pmui-fg-status-negative-default',
      }}
    />
  );
}

export const Select = SelectComponent;
